import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { Observable, of, combineLatest, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Client, Bike, WorkshopWork, WorkCurrentStatus } from '../core/models';
import { FirestoreDataService } from '../core/firestore-data.service';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-clients',
  templateUrl: './clients.component.html',
  styleUrls: ['./clients.component.css'],
  providers: [DatePipe]
})
export class ClientsComponent implements OnInit, OnDestroy {
  allClients$: Observable<Client[]>;
  filteredClients$: Observable<Client[]>;
  clientSearchControl: FormControl;

  private allClients: Client[] = [];
  filteredClients: Client[] = [];  
  isLoadingClients = true;
  selectedClient: Client | null = null;
  clientBikes$: Observable<Bike[]> = of([]);

  clientForm!: FormGroup;
  bikeForm!: FormGroup;
  get vehicleForm(): FormGroup {
    return this.bikeForm;
  }

  isSavingClient = false;
  isSavingBike = false;
  isDeleting = false;
  isNewClientMode = false;

  // --- VISTA COMPLETA E HISTORIAL DE TRABAJOS ---
  isFullViewMode = false;
  historyWorks: WorkshopWork[] = [];
  isLoadingHistoryWorks = false;
  selectedHistoryWork: WorkshopWork | null = null;
  activeHistoryFilter: 'all' | 'bike' | null = null;
  selectedBikeForHistory: Bike | null = null;
  private historySubscription?: Subscription;

  constructor(
    private firestoreDataService: FirestoreDataService,
    private fb: FormBuilder,
    private datePipe: DatePipe
  ) {
    this.clientSearchControl = new FormControl('');
    
    // 1. Obtenemos el observable de clientes del servicio
    this.allClients$ = this.firestoreDataService.clients$;

    this.clientForm = this.fb.group({
      id: [''],
      name: ['', Validators.required],
      phone: [''],
      email: ['', [Validators.email]],
      cedula: [''],
      weightRange: [null],
      cyclistLevel: [null]
    });

    this.bikeForm = this.fb.group({
      brand: ['', Validators.required],
      model: [''],
      color: ['', Validators.required],
      chassis: [''],
      year: [''],
      category: [null],
      frameMaterial: [null],
      isEBike: [false],
      usageFrequency: [null],
      suspensionType: [null],
      brakeType: [null],
      drivetrainLevel: [null],
      isActive: [true]
    });

    // 2. Creamos el observable de clientes filtrados
    // Esta es la lógica corregida que soluciona el error
     this.filteredClients$ = combineLatest([
      this.firestoreDataService.clients$, // Escucha el estado global de clientes
      this.clientSearchControl.valueChanges.pipe(startWith('')) // Escucha el input de búsqueda
    ]).pipe(
      map(([clients, searchTerm]) => {
        if (!searchTerm) return clients;
        const term = searchTerm.toLowerCase();
        return clients.filter(c => 
          c.name.toLowerCase().includes(term) || 
          c.phone?.includes(term) || 
          c.email?.toLowerCase().includes(term)
        );
      })
    );
  }

 ngOnInit(): void {
    // Suscribirse a la lista completa de clientes
    this.firestoreDataService.loadClients();

    // Suscribirse a los cambios en el campo de búsqueda
    this.clientSearchControl.valueChanges.subscribe(searchTerm => {
      this.filterClients(searchTerm);
    });
  }

   private filterClients(searchTerm: string | null): void {
    if (!searchTerm) {
      this.filteredClients = this.allClients;
      return;
    }
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    this.filteredClients = this.allClients.filter((client: Client) =>
      (client.name?.toLowerCase().includes(lowerCaseSearchTerm)) ||
      (client.phone?.includes(lowerCaseSearchTerm)) ||
      (client.email?.toLowerCase().includes(lowerCaseSearchTerm))
    );
  }

  ngOnDestroy(): void {
    if (this.historySubscription) {
      this.historySubscription.unsubscribe();
    }
  }

  selectClient(client: Client): void {
    this.isNewClientMode = false;
    this.selectedClient = client;
    this.selectedHistoryWork = null;
    
    // El patchValue ahora es seguro porque clientForm se inició en el constructor
    this.clientForm.patchValue({
      id: client.id,
      name: client.name,
      phone: client.phone || '',
      email: client.email || '',
      cedula: client.cedula || '',
      weightRange: client.weightRange || null,
      cyclistLevel: client.cyclistLevel || null
    });

    if (client.id) {
      this.clientBikes$ = this.firestoreDataService.getBikesForClient(client.id);
      if (this.isFullViewMode) {
        this.loadClientWorks();
      }
    }
  }

  startNewClient(): void {
    this.selectedClient = null;
    this.isFullViewMode = false;
    this.historyWorks = [];
    this.selectedHistoryWork = null;
    this.activeHistoryFilter = null;
    this.selectedBikeForHistory = null;
    this.clientForm.reset({
      id: '',
      name: '',
      phone: '',
      email: '',
      cedula: '',
      weightRange: null,
      cyclistLevel: null
    });
    this.isNewClientMode = true;
  }

  clearSelection(): void {
    this.selectedClient = null;
    this.isFullViewMode = false;
    this.historyWorks = [];
    this.selectedHistoryWork = null;
    this.activeHistoryFilter = null;
    this.selectedBikeForHistory = null;
    if (this.historySubscription) {
      this.historySubscription.unsubscribe();
    }
    this.clientForm.reset({
      id: '',
      name: '',
      phone: '',
      email: '',
      cedula: '',
      weightRange: null,
      cyclistLevel: null
    });
    this.bikeForm.reset({
      brand: '',
      model: '',
      color: '',
      chassis: '',
      year: '',
      category: null,
      frameMaterial: null,
      isEBike: false,
      usageFrequency: null,
      suspensionType: null,
      brakeType: null,
      drivetrainLevel: null,
      isActive: true
    });
    this.isNewClientMode = false;
  }

  async onSaveClient(): Promise<void> {
    if (this.clientForm.invalid) {
      alert("Por favor, complete los campos requeridos para el cliente.");
      return;
    }

    this.isSavingClient = true;
    const clientData = this.clientForm.value;

    try {
      if (this.isNewClientMode) {
        // --- NUEVA VALIDACIÓN ---
        const { exists, field } = await this.firestoreDataService.checkIfClientExists(clientData);
        if (exists) {
          alert(`Error: Ya existe un cliente con este ${field === 'email' ? 'correo electrónico' : 'número de teléfono'}.`);
          this.isSavingClient = false;
          return;
        }
        // --- FIN DE LA VALIDACIÓN ---
        
        const { name, phone, email, cedula, weightRange, cyclistLevel } = clientData;
        await this.firestoreDataService.createClient({ 
          name, 
          phone, 
          email,
          cedula: cedula || '',
          weightRange: weightRange || null,
          cyclistLevel: cyclistLevel || null
        });
        alert(`¡Cliente "${name}" creado exitosamente!`);
      } else if (this.selectedClient?.id) {
        await this.firestoreDataService.updateClient(this.selectedClient.id, clientData);
        alert("¡Cliente actualizado exitosamente!");
      }
      this.clearSelection();
    } catch (error: any) {
      alert(`Error al guardar el cliente: ${error.message}`);
    } finally {
      this.isSavingClient = false;
    }
  }

  async onAddBike(): Promise<void> {
    if (this.bikeForm.invalid || !this.selectedClient?.id) {
      alert("Por favor, complete los campos requeridos para la bicicleta.");
      return;
    }

    this.isSavingBike = true;
    const formValues = this.bikeForm.value;
    const bikeData: Omit<Bike, 'id'> = {
      clientId: this.selectedClient.id,
      ...formValues,
      isActive: formValues.isActive !== false
    };

    try {
      await this.firestoreDataService.addBike(bikeData);
      alert("¡Bicicleta agregada exitosamente!");
      this.bikeForm.reset({
        brand: '',
        model: '',
        color: '',
        chassis: '',
        year: '',
        category: null,
        frameMaterial: null,
        isEBike: false,
        usageFrequency: null,
        suspensionType: null,
        brakeType: null,
        drivetrainLevel: null,
        isActive: true
      });
      // Forzar la recarga de las bicicletas
      this.clientBikes$ = this.firestoreDataService.getBikesForClient(this.selectedClient.id);
    } catch (error: any) {
      alert(`Error al agregar la bicicleta: ${error.message}`);
    } finally {
      this.isSavingBike = false;
    }
  }

  async toggleBikeStatus(bike: Bike): Promise<void> {
    if (!bike?.id || !this.selectedClient?.id) return;
    const newStatus = bike.isActive === false ? true : false;
    try {
      await this.firestoreDataService.updateBike(bike.id, { isActive: newStatus });
      this.clientBikes$ = this.firestoreDataService.getBikesForClient(this.selectedClient.id);
    } catch (error: any) {
      alert(`Error al cambiar el estado de la bicicleta: ${error.message}`);
    }
  }

  async onDeleteClient(): Promise<void> {
    if (!this.selectedClient?.id) {
      alert("Por favor, seleccione un cliente para eliminar.");
      return;
    }
    
    const confirmation = window.confirm(`¿Está seguro de que desea eliminar a "${this.selectedClient.name}"?\nEsta acción no se puede deshacer.`);
    
    if (confirmation) {
      this.isDeleting = true;
      try {
        await this.firestoreDataService.deleteClient(this.selectedClient.id);
        alert("Cliente eliminado exitosamente.");
        this.clearSelection();
      } catch (error: any) {
        alert(`Error al eliminar el cliente: ${error.message}`);
      } finally {
        this.isDeleting = false;
      }
    }
  }

  // --- MODALIDAD VISTA COMPLETA (VER CLIENTE) ---
  openFullClientView(): void {
    if (!this.selectedClient) return;
    this.isFullViewMode = true;
    this.loadClientWorks();
  }

  exitFullClientView(): void {
    this.isFullViewMode = false;
    this.selectedHistoryWork = null;
  }

  loadClientWorks(): void {
    if (!this.selectedClient?.id) return;
    this.isLoadingHistoryWorks = true;
    this.activeHistoryFilter = 'all';
    this.selectedBikeForHistory = null;
    this.selectedHistoryWork = null;

    if (this.historySubscription) {
      this.historySubscription.unsubscribe();
    }

    this.historySubscription = this.firestoreDataService.getWorksForClient(this.selectedClient.id).subscribe({
      next: (works) => {
        this.historyWorks = works;
        this.isLoadingHistoryWorks = false;
      },
      error: (err) => {
        console.error("Error al cargar historial de trabajos:", err);
        this.historyWorks = [];
        this.isLoadingHistoryWorks = false;
      }
    });
  }

  loadBikeWorks(bike: Bike): void {
    if (!bike?.id) return;
    this.isLoadingHistoryWorks = true;
    this.activeHistoryFilter = 'bike';
    this.selectedBikeForHistory = bike;
    this.selectedHistoryWork = null;

    if (this.historySubscription) {
      this.historySubscription.unsubscribe();
    }

    this.historySubscription = this.firestoreDataService.getWorksForBike(bike.id).subscribe({
      next: (works) => {
        this.historyWorks = works;
        this.isLoadingHistoryWorks = false;
      },
      error: (err) => {
        console.error("Error al cargar historial de la bicicleta:", err);
        this.historyWorks = [];
        this.isLoadingHistoryWorks = false;
      }
    });
  }

  selectHistoryWork(work: WorkshopWork): void {
    this.selectedHistoryWork = work;
  }

  // --- IMPRIMIR HISTORIAL DE MANTENIMIENTOS DE LA BICICLETA ---
  async printBikeMaintenanceHistory(bike: Bike): Promise<void> {
    if (!bike?.id || !this.selectedClient) {
      alert("No se pudo identificar la bicicleta o el cliente.");
      return;
    }

    const client = this.selectedClient;
    const works = await this.firestoreDataService.getWorksForBikeOnce(bike.id);

    const printWindow = window.open('', '_blank', 'height=900,width=850');
    if (!printWindow) {
      alert("Su navegador bloqueó la ventana emergente. Por favor permítalas para imprimir.");
      return;
    }

    const totalInvested = works.reduce((sum, w) => sum + (w.totalPrice || 0), 0);
    const currentDate = new Date().toLocaleString('es-DO');

    printWindow.document.write('<html><head><title>Historial de Mantenimientos - ' + bike.brand + ' ' + (bike.model || '') + '</title>');
    printWindow.document.write(`<style>
      body {
        font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
        padding: 24px;
        color: #222;
        max-width: 850px;
        margin: 0 auto;
        font-size: 13px;
        line-height: 1.4;
      }
      .header-wrap {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 2px solid #0f172a;
        padding-bottom: 12px;
        margin-bottom: 16px;
      }
      .logo {
        max-height: 55px;
        max-width: 180px;
        object-fit: contain;
      }
      .company-info {
        text-align: right;
      }
      .company-info h2 {
        margin: 0 0 4px 0;
        font-size: 18px;
        letter-spacing: 0.5px;
        color: #0f172a;
      }
      .company-info p {
        margin: 1px 0;
        font-size: 11px;
        color: #555;
      }
      .report-title-bar {
        background-color: #f1f5f9;
        border-left: 4px solid #0d6efd;
        padding: 8px 12px;
        margin-bottom: 16px;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .report-title-bar h3 {
        margin: 0;
        font-size: 15px;
        color: #0f172a;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      .info-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 6px;
        padding: 12px 16px;
        margin-bottom: 18px;
      }
      .info-col p {
        margin: 3px 0;
        font-size: 12px;
      }
      .summary-badges {
        display: flex;
        gap: 15px;
        margin-bottom: 18px;
      }
      .summary-box {
        flex: 1;
        background: #fff;
        border: 1px solid #cbd5e1;
        border-radius: 6px;
        padding: 8px 12px;
        text-align: center;
      }
      .summary-box .val {
        font-size: 16px;
        font-weight: bold;
        color: #0d6efd;
      }
      .summary-box .lbl {
        font-size: 11px;
        color: #64748b;
        text-transform: uppercase;
      }
      .section-heading {
        font-size: 14px;
        font-weight: bold;
        color: #1e293b;
        border-bottom: 1px solid #e2e8f0;
        padding-bottom: 6px;
        margin: 20px 0 12px 0;
        text-transform: uppercase;
        letter-spacing: 0.3px;
      }
      .visit-card {
        border: 1px solid #cbd5e1;
        border-radius: 6px;
        margin-bottom: 14px;
        padding: 12px 14px;
        background: #fff;
        page-break-inside: avoid;
      }
      .visit-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px dashed #cbd5e1;
        padding-bottom: 6px;
        margin-bottom: 8px;
      }
      .visit-header h4 {
        margin: 0;
        font-size: 13px;
        color: #0f172a;
      }
      .badge {
        display: inline-block;
        padding: 2px 7px;
        font-size: 10px;
        font-weight: bold;
        border-radius: 4px;
        background: #e2e8f0;
        color: #334155;
      }
      .badge-success { background: #dcfce7; color: #166534; }
      .labors-list {
        margin: 4px 0 6px 18px;
        padding: 0;
        font-size: 12px;
      }
      .labors-list li {
        margin-bottom: 2px;
      }
      .parts-text {
        font-size: 12px;
        color: #475569;
        margin-top: 4px;
      }
      .visit-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 8px;
        padding-top: 6px;
        border-top: 1px dashed #f1f5f9;
        font-size: 12px;
      }
      .visit-total {
        font-weight: bold;
        font-size: 13px;
        color: #0f172a;
      }
      .footer-signatures {
        margin-top: 40px;
        display: flex;
        justify-content: space-between;
        page-break-inside: avoid;
      }
      .sig-line {
        width: 40%;
        text-align: center;
        border-top: 1px solid #94a3b8;
        padding-top: 6px;
        font-size: 11px;
        color: #475569;
      }
      .brand-footer {
        margin-top: 25px;
        text-align: center;
        font-size: 10px;
        color: #94a3b8;
        border-top: 1px solid #f1f5f9;
        padding-top: 8px;
      }
    </style>`);
    printWindow.document.write('</head><body>');

    // Header con logo y datos de empresa
    printWindow.document.write(`
      <div class="header-wrap">
        <div>
          <img src="assets/logo.png" alt="Enduro Bikes Dominicana" class="logo">
        </div>
        <div class="company-info">
          <h2>ENDURO BIKES DOMINICANA</h2>
          <p>ServiTrack - Servicio Técnico Especializado de Bicicletas</p>
          <p>Tel: (849) 846-8391</p>
          <p>Santo Domingo, República Dominicana</p>
        </div>
      </div>
    `);

    // Barra de título del reporte
    printWindow.document.write(`
      <div class="report-title-bar">
        <div>
          <h3>Historial de Mantenimientos y Servicios</h3>
          <span style="font-size:11px; color:#64748b;">Ficha técnica de seguimiento vehicular</span>
        </div>
        <div style="font-size:11px; color:#64748b;">Emitido el: ${currentDate}</div>
      </div>
    `);

    // Info de la Bicicleta y Propietario
    printWindow.document.write(`
      <div class="info-grid">
        <div class="info-col">
          <p><strong>Bicicleta:</strong> ${bike.brand} ${bike.model || ''} - ${bike.color}</p>
          <p><strong>Chasis / N° Serie:</strong> ${bike.chassis || 'N/A'}</p>
          <p><strong>Año:</strong> ${bike.year || 'N/A'} ${bike.isEBike ? ' | ⚡ E-Bike' : ''}</p>
          <p><strong>Categoría / Cuadro:</strong> ${bike.category || 'N/A'} ${bike.frameMaterial ? ' | ' + bike.frameMaterial : ''}</p>
        </div>
        <div class="info-col">
          <p><strong>Propietario:</strong> ${client.name}</p>
          <p><strong>Teléfono:</strong> ${client.phone || 'N/A'}</p>
          <p><strong>Email:</strong> ${client.email || 'N/A'}</p>
          <p><strong>Cédula:</strong> ${client.cedula || 'N/A'}</p>
        </div>
      </div>
    `);

    // Resumen estadístico
    printWindow.document.write(`
      <div class="summary-badges">
        <div class="summary-box">
          <div class="val">${works.length}</div>
          <div class="lbl">Visitas / Servicios Registrados</div>
        </div>
        <div class="summary-box">
          <div class="val">RD$ ${totalInvested.toLocaleString('es-DO', { minimumFractionDigits: 2 })}</div>
          <div class="lbl">Inversión Total en Taller</div>
        </div>
      </div>
    `);

    // Listado de Visitas
    printWindow.document.write('<div class="section-heading">Registro Cronológico de Visitas</div>');

    if (works.length === 0) {
      printWindow.document.write('<p style="text-align: center; color: #64748b; padding: 25px;">No hay registros de servicios o mantenimientos para esta bicicleta.</p>');
    } else {
      works.forEach((w, idx) => {
        const dateStr = this.datePipe.transform(w.entryDate?.toDate ? w.entryDate.toDate() : w.entryDate, 'dd/MM/yyyy HH:mm', 'es') || 'Fecha no registrada';
        
        // Labores realizadas (summarized)
        const laborItems = (w.addedItems || []).filter(i => i.itemType === 'Labor');
        const partItems = (w.addedItems || []).filter(i => i.itemType === 'Part');
        
        printWindow.document.write('<div class="visit-card">');
        printWindow.document.write(`
          <div class="visit-header">
            <h4>#${works.length - idx} — Orden: <strong>${w.caseNumber}</strong> | ${w.serviceType || 'Mantenimiento General'}</h4>
            <div>
              <span class="badge ${w.currentStatus === 'Completed' || w.currentStatus === 'Ready' || w.currentStatus === 'Handed Off' ? 'badge-success' : ''}">
                ${this.getSpanishStatus(w.currentStatus)}
              </span>
            </div>
          </div>
          <div style="font-size: 11px; color: #64748b; margin-bottom: 6px;">
            <strong>Fecha de Recepción:</strong> ${dateStr}
          </div>
        `);

        if (w.description) {
          printWindow.document.write(`<div style="font-size:12px; margin-bottom:6px;"><strong>Motivo / Descripción:</strong> ${w.description}</div>`);
        }

        if (laborItems.length > 0) {
          printWindow.document.write('<div><strong style="font-size:12px;">Labores Realizadas:</strong><ul class="labors-list">');
          laborItems.forEach(li => {
            const mechanicInfo = li.mechanicName ? ` <span style="color:#64748b;">(Mec: ${li.mechanicName})</span>` : '';
            printWindow.document.write(`<li>${li.productName} (x${li.quantity})${mechanicInfo}</li>`);
          });
          printWindow.document.write('</ul></div>');
        } else if (!w.addedItems || w.addedItems.length === 0) {
          printWindow.document.write('<div style="font-size:12px; color:#64748b; font-style:italic; margin-bottom:4px;">Servicio preventivo / inspección general.</div>');
        }

        if (partItems.length > 0) {
          const partsStr = partItems.map(p => `${p.productName} (x${p.quantity})`).join(', ');
          printWindow.document.write(`<div class="parts-text"><strong>Repuestos / Piezas:</strong> ${partsStr}</div>`);
        }

        if (w.clientItems && w.clientItems.length > 0) {
          printWindow.document.write(`<div style="font-size:11px; color:#854d0e; margin-top:4px;"><strong>Piezas del cliente devueltas:</strong> ${w.clientItems.join(', ')}</div>`);
        }

        printWindow.document.write(`
          <div class="visit-footer">
            <span style="color:#64748b;">Estado: ${this.getSpanishStatus(w.currentStatus)}</span>
            <span class="visit-total">Costo: RD$ ${(w.totalPrice || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}</span>
          </div>
        `);

        printWindow.document.write('</div>');
      });
    }

    // Firmas
    printWindow.document.write(`
      <div class="footer-signatures">
        <div class="sig-line">Firma Taller / Mecánico Responsable</div>
        <div class="sig-line">Firma de Conformidad del Cliente</div>
      </div>
      <div class="brand-footer">
        Enduro Bikes Dominicana — Especialistas en ciclismo y servicio técnico de alta precisión.
      </div>
    `);

    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 400);
  }

  // --- IMPRIMIR UNA ORDEN ESPECÍFICA ---
  async printSingleWork(work: WorkshopWork, client: Client, bike?: Bike): Promise<void> {
    const printWindow = window.open('', '_blank', 'height=800,width=800');
    if (!printWindow) {
      alert("Su navegador bloqueó la ventana emergente. Por favor permita las ventanas emergentes.");
      return;
    }

    const bikeInfo = bike || { brand: work.bikeDescription || 'Bicicleta', model: '', color: '', chassis: '' } as Bike;

    printWindow.document.write('<html><head><title>Detalles de Orden - ' + work.caseNumber + '</title>');
    printWindow.document.write(`<style>
        body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; padding: 20px; color: #333; max-width: 800px; margin: 0 auto; }
        .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
        .row { display: flex; justify-content: space-between; margin-bottom: 10px; }
        .section-title { font-size: 16px; font-weight: bold; background: #eee; padding: 8px; margin-top: 25px; margin-bottom: 10px; border-radius: 4px; }
        table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 14px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f8f9fa; }
        .text-right { text-align: right; }
    </style>`);
    printWindow.document.write('</head><body>');
    
    // Cabecera
    printWindow.document.write(`<div class="header">
      <h2>Reporte de Orden: ${work.caseNumber}</h2>
      <p style="margin:0; color:#666;">Emitido el: ${new Date().toLocaleString('es-DO')}</p>
    </div>`);
    
    // Info del Cliente y Bici
    printWindow.document.write('<div class="row">');
    printWindow.document.write(`<div><strong>Cliente:</strong> ${client.name}<br><strong>Teléfono:</strong> ${client.phone || 'N/A'}</div>`);
    printWindow.document.write(`<div><strong>Fecha de Recepción:</strong> ${this.datePipe.transform(work.entryDate?.toDate ? work.entryDate.toDate() : work.entryDate, 'dd/MM/yyyy HH:mm', 'es')}<br><strong>Estado Actual:</strong> ${this.getSpanishStatus(work.currentStatus)}</div>`);
    printWindow.document.write('</div>');

    printWindow.document.write('<div class="row">');
    printWindow.document.write(`<div><strong>Bicicleta:</strong> ${bikeInfo.brand} ${bikeInfo.model || ''} - ${bikeInfo.color}<br><strong>Chasis:</strong> ${bikeInfo.chassis || 'N/A'}</div>`);
    printWindow.document.write(`<div><h3 style="margin:0;">Total: RD$ ${work.totalPrice}</h3></div>`);
    printWindow.document.write('</div>');

    if (work.description) {
      printWindow.document.write(`<div style="margin-top: 10px;"><strong>Descripción / Motivo:</strong> ${work.description}</div>`);
    }

    // Artículos traídos por el cliente
    if (work.clientItems && work.clientItems.length > 0) {
      printWindow.document.write('<div class="section-title">Artículos / Piezas Traídas por el Cliente (Devueltos)</div>');
      printWindow.document.write('<ul style="margin: 5px 0; padding-left: 20px;">');
      work.clientItems.forEach(ci => {
        printWindow.document.write(`<li><strong>${ci}</strong></li>`);
      });
      printWindow.document.write('</ul>');
    }

    // Artículos
    printWindow.document.write('<div class="section-title">Artículos y Servicios Añadidos</div>');
    if (work.addedItems && work.addedItems.length > 0) {
        printWindow.document.write('<table><thead><tr><th>Descripción</th><th>Asignación</th><th>Cant.</th><th class="text-right">Precio Unit.</th><th class="text-right">Subtotal</th></tr></thead><tbody>');
        work.addedItems.forEach(item => {
            const subtotal = item.quantity * item.unitPrice;
            const assignment = (item.mechanicName || item.assistantName) 
              ? `${item.mechanicName ? 'Mec:'+item.mechanicName : ''} ${item.assistantName ? 'Asis:'+item.assistantName : ''}` 
              : 'N/A';
            printWindow.document.write(`<tr>
              <td>${item.productName}</td>
              <td style="font-size:12px; color:#555;">${assignment}</td>
              <td>${item.quantity}</td>
              <td class="text-right">RD$ ${item.unitPrice}</td>
              <td class="text-right">RD$ ${subtotal}</td>
            </tr>`);
        });
        printWindow.document.write('</tbody></table>');
    } else {
        printWindow.document.write('<p>No hay artículos registrados en esta orden.</p>');
    }

    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 400);
  }

  getSpanishStatus(statusValue: WorkCurrentStatus | undefined): string {
    if (!statusValue) return 'Desconocido';
    const statusMap: Record<WorkCurrentStatus, string> = {
      'Received': 'Recibido',
      'In Progress': 'En Progreso',
      'Awaiting Parts': 'Esperando Repuestos',
      'Ready': 'Lista',
      'Completed': 'Completado',
      'Cancelled': 'Cancelado/Desestimado',
      'Handed Off': 'Entregada'
    };
    return statusMap[statusValue] || statusValue;
  }

  getStatusClass(status: WorkCurrentStatus | undefined): string {
    if (!status) return 'bg-secondary';
    switch (status) {
      case 'Received': return 'bg-primary';
      case 'In Progress': return 'bg-info text-dark';
      case 'Awaiting Parts': return 'bg-warning text-dark';
      case 'Ready': return 'bg-success';
      case 'Completed': return 'bg-dark';
      case 'Cancelled': return 'bg-danger';
      case 'Handed Off': return 'bg-success text-white';
      default: return 'bg-light text-dark';
    }
  }
}